/* @refresh reload */
import { render } from "solid-js/web";

import { App } from "@/app";
import "@/lib/client-telemetry";
import "@/styles/global.css";

// PWA: auto-update in a SINGLE reload. The `sw.js` (workbox) already does `skipWaiting()` on receiving
// `{type:"SKIP_WAITING"}`; here, on detecting a new INSTALLED SW while a controller already exists (=it is
// an update, not the first installation), we ask it to → it takes control → `controllerchange`
// → we reload ONCE. This way a single user refresh is enough (before, the new SW stayed `waiting`).
// Dependency-free on purpose (avoids workbox-window). To make it FULLY automatic (reload
// inactive tabs with no user action) see infra/PWA_UPDATES.md. In Tauri there is no SW → no-op.
if ("serviceWorker" in navigator) {
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) {
      return;
    }
    reloading = true;
    window.location.reload();
  });
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js", { scope: "/" }).then((reg) => {
      // In environments where the SW is blocked (e.g. Playwright with
      // `serviceWorkers: "block"`) or denied, `register()` can resolve without
      // a usable registration. Without this guard, `reg.addEventListener`
      // would throw "Cannot read properties of undefined" as a pageerror.
      if (!reg) {
        return undefined;
      }
      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        if (!next) {
          return;
        }
        next.addEventListener("statechange", () => {
          // Only on UPDATE (there is already a controller); on the 1st install we do not reload.
          if (next.state === "installed" && navigator.serviceWorker.controller) {
            // ServiceWorker.postMessage(message, transfer): the 2nd arg is the list of
            // transferables, not a targetOrigin (that is Window.postMessage). We pass
            // [] explicitly to satisfy the linter rule without changing the semantics.
            next.postMessage({ type: "SKIP_WAITING" }, []);
          }
        });
      });
      return undefined;
    });
  });
}

const root = document.querySelector("#root");

if (!root) {
  throw new Error("Root element #root not found in index.html");
}

render(() => <App />, root);

function dismissAppLoader() {
  const loader = document.querySelector("#app-loader");
  if (!loader) {
    return;
  }
  loader.classList.add("app-loader--hidden");
  const LOADER_FADE_MS = 300;
  setTimeout(() => {
    loader.remove();
  }, LOADER_FADE_MS);
}
requestAnimationFrame(() => {
  requestAnimationFrame(dismissAppLoader);
});

// Tell the index.html boot watchdog the app mounted: cancels the self-heal
// reload and clears its one-shot guard (so a future stale-deploy can recover).
(window as typeof window & { __appBootOk?: () => void }).__appBootOk?.();
